import { useEffect, useState } from "react";
import { ArrowDownToLine, ArrowUpFromLine, Pencil, Plus } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { act, useFetch } from "@/lib/hooks";
import { can, da, fmtDate } from "@/lib/format";
import { Empty, Field, Loading, PageHeader, Panel, Table, cellCls } from "@/components/common";
import { selectCls } from "@/components/forms";
import { PackagesPanel } from "@/components/Packages";
import { BulkStockDialog } from "@/components/BulkStock";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

function ItemDialog({ item, open, onOpenChange, onSaved, tva, categories }: any) {
  const empty = { kind: "service", code: "", label: "", category: "", unit_price: 0, tva_rate: tva, unit: "u", min_stock: 0, active: true };
  const [f, setF] = useState<any>(empty);
  useEffect(() => {
    if (open) setF(item ? { ...empty, ...item } : empty);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);
  const save = async () => {
    const body = { ...f, unit_price: Number(f.unit_price), tva_rate: Number(f.tva_rate), min_stock: Number(f.min_stock) };
    if (await act(() => (item ? api.put(`/items/${item.id}`, body) : api.post("/items", body)), "Article enregistré")) {
      onSaved();
      onOpenChange(false);
    }
  };
  const set = (k: string) => (e: any) => setF({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="item-dialog">
        <DialogHeader><DialogTitle className="font-display uppercase">{item ? "Modifier l'article" : "Nouvel article"}</DialogTitle></DialogHeader>
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Type">
            <select className={selectCls} value={f.kind} onChange={set("kind")} disabled={!!item} data-testid="item-kind-select">
              <option value="part">Pièce (stockée)</option><option value="service">Prestation / main d'œuvre</option>
            </select>
          </Field>
          <Field label="Code"><Input value={f.code} onChange={set("code")} data-testid="item-code-input" /></Field>
          <Field label="Libellé" className="sm:col-span-2"><Input value={f.label} onChange={set("label")} data-testid="item-label-input" /></Field>
          <Field label="Catégorie" className="sm:col-span-2">
            <Input value={f.category || ""} onChange={set("category")} list="item-categories" placeholder="Entretien, Freinage…" data-testid="item-category-input" />
            <datalist id="item-categories">{categories.map((c: string) => <option key={c} value={c} />)}</datalist>
          </Field>
          <Field label="Prix unitaire HT (DA)"><Input type="number" value={f.unit_price} onChange={set("unit_price")} data-testid="item-price-input" /></Field>
          <Field label="TVA %"><Input type="number" value={f.tva_rate} onChange={set("tva_rate")} data-testid="item-tva-input" /></Field>
          {f.kind === "part" && <Field label="Stock minimum"><Input type="number" value={f.min_stock} onChange={set("min_stock")} data-testid="item-minstock-input" /></Field>}
          <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={f.active} onChange={set("active")} data-testid="item-active-checkbox" />Actif</label>
        </div>
        <Button className="h-11" onClick={save} data-testid="item-save-btn">Enregistrer</Button>
      </DialogContent>
    </Dialog>
  );
}

function MoveDialog({ item, onClose, onSaved }: any) {
  const [type, setType] = useState("entree");
  const [qty, setQty] = useState(1);
  const [reason, setReason] = useState("");
  const save = async () => {
    if (await act(() => api.post("/stock/movements", { item_id: item.id, type, qty, reason }), "Stock mis à jour")) {
      onSaved();
      onClose();
    }
  };
  return (
    <Dialog open={!!item} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="stock-move-dialog">
        <DialogHeader><DialogTitle className="font-display uppercase">Mouvement de stock · {item?.label}</DialogTitle></DialogHeader>
        <p className="text-sm text-muted-foreground">Stock actuel : <b>{item?.stock_qty}</b></p>
        <div className="grid grid-cols-2 gap-2">
          {[["entree", "Entrée"], ["sortie", "Sortie"]].map(([v, l]) => (
            <button key={v} onClick={() => setType(v)} className={`h-12 rounded-sm border text-sm font-semibold ${type === v ? "border-primary bg-primary/10" : "border-border"}`} data-testid={`stock-type-${v}`}>{l}</button>
          ))}
        </div>
        <Field label="Quantité"><Input type="number" value={qty} onChange={(e) => setQty(Number(e.target.value))} data-testid="stock-qty-input" /></Field>
        <Field label="Motif"><Input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Livraison fournisseur, casse…" data-testid="stock-reason-input" /></Field>
        <Button className="h-11" onClick={save} data-testid="stock-save-btn">Valider</Button>
      </DialogContent>
    </Dialog>
  );
}

function PriceCell({ item, editable, onSaved }: { item: any; editable: boolean; onSaved: () => void }) {
  const [v, setV] = useState<any>(item.unit_price);
  useEffect(() => setV(item.unit_price), [item.unit_price]);
  const missing = !item.unit_price;
  if (!editable) return <span className={`font-mono-data ${missing ? "text-amber-400" : ""}`}>{missing ? "Prix à définir" : da(item.unit_price)}</span>;
  const save = async () => {
    if (v === "" || Number(v) === item.unit_price) {
      setV(item.unit_price);
      return;
    }
    if (await act(() => api.patch(`/items/${item.id}/price`, { unit_price: Number(v) }), "Prix enregistré")) onSaved();
    else setV(item.unit_price);
  };
  return (
    <div className="flex items-center gap-2">
      <Input type="number" min={0} value={v} onChange={(e) => setV(e.target.value)} onBlur={save} onKeyDown={(e) => e.key === "Enter" && (e.target as HTMLInputElement).blur()}
        className={`h-9 w-32 font-mono-data ${missing ? "border-amber-500/70" : ""}`} data-testid={`item-price-inline-${item.id}`} />
      <span className="text-xs text-muted-foreground">DA HT</span>
    </div>
  );
}

export default function Catalog() {
  const { user } = useAuth();
  const items = useFetch<any[]>("/items");
  const settings = useFetch<any>("/settings");
  const stockPerm = can(user?.role, "stock");
  const moves = useFetch<any[]>(stockPerm ? "/stock/movements" : null);
  const [edit, setEdit] = useState<any>(undefined);
  const [move, setMove] = useState<any>(null);
  const write = can(user?.role, "catalog_write");
  const refresh = () => { items.reload(); moves.reload(); };
  const [cat, setCat] = useState("");
  const [bulk, setBulk] = useState(false);
  const categories = Array.from(new Set((items.data || []).filter((i) => i.active).map((i) => i.category || "Autres"))).sort((a, b) => a.localeCompare(b, "fr"));
  const shown = (items.data || []).filter((i) => !cat || (i.category || "Autres") === cat);
  const toPrice = (items.data || []).filter((i) => i.active && !i.unit_price).length;
  return (
    <div>
      <PageHeader eyebrow="Prestations & pièces" title="Catalogue & stock"
        actions={<>
          {stockPerm && <Button variant="outline" className="h-11" onClick={() => setBulk(true)} data-testid="bulk-stock-btn"><ArrowDownToLine className="mr-2 h-4 w-4" />Entrée de stock</Button>}
          {write && <Button className="h-11" onClick={() => setEdit(null)} data-testid="new-item-btn"><Plus className="mr-2 h-4 w-4" />Article</Button>}
        </>} />
      <Tabs defaultValue="items">
        <TabsList><TabsTrigger value="items" data-testid="tab-items">Articles</TabsTrigger><TabsTrigger value="packages" data-testid="tab-packages">Forfaits</TabsTrigger>{stockPerm && <TabsTrigger value="moves" data-testid="tab-moves">Mouvements</TabsTrigger>}</TabsList>
        <TabsContent value="packages"><PackagesPanel items={items.data || []} write={write} /></TabsContent>
        <TabsContent value="items">
          {write && toPrice > 0 && <div className="mb-4 rounded-sm border border-amber-500/40 bg-amber-500/10 p-3 text-sm text-amber-200 [.light_&]:text-amber-800" data-testid="prices-missing-banner">{toPrice} prestation(s) ou pièce(s) sans prix : saisissez vos tarifs directement dans la colonne « Prix HT ». Ils seront proposés automatiquement sur les devis et factures, et restent modifiables ligne par ligne.</div>}
          <div className="mb-4 flex flex-wrap gap-2">
            {["", ...categories].map((c) => (
              <button key={c || "all"} onClick={() => setCat(c)} className={`h-9 rounded-sm border px-3 text-sm transition-colors ${cat === c ? "border-primary bg-primary/10" : "border-border hover:bg-accent"}`} data-testid={`catalog-cat-${c || "all"}`}>{c || "Tout"}</button>
            ))}
          </div>
          <Panel>
            {items.loading ? <Loading /> : (
              <Table head={["Code", "Libellé", "Catégorie", "Prix HT", "TVA", "Stock", ""]} testId="items-table">
                {shown.map((it) => {
                  const low = it.kind === "part" && it.stock_qty <= it.min_stock;
                  return (
                    <tr key={it.id} className={`border-b border-border/60 ${it.active ? "" : "opacity-50"}`} data-testid={`item-row-${it.id}`}>
                      <td className={`${cellCls} font-mono-data text-xs`}>{it.code}</td>
                      <td className={cellCls}>{it.label}</td>
                      <td className={`${cellCls} text-xs text-muted-foreground`}>{it.kind === "part" ? "Pièce" : it.category || "Prestation"}</td>
                      <td className={cellCls}><PriceCell item={it} editable={write} onSaved={items.reload} /></td>
                      <td className={`${cellCls} font-mono-data`}>{it.tva_rate} %</td>
                      <td className={`${cellCls} font-mono-data ${low ? "font-bold text-amber-400" : ""}`} data-testid={`item-stock-${it.id}`}>{it.kind === "part" ? it.stock_qty : "—"}</td>
                      <td className={`${cellCls} text-right`}>
                        {stockPerm && it.kind === "part" && <Button size="sm" variant="ghost" onClick={() => setMove(it)} data-testid={`item-move-btn-${it.id}`}><ArrowDownToLine className="h-4 w-4" /></Button>}
                        {write && <Button size="sm" variant="ghost" onClick={() => setEdit(it)} data-testid={`item-edit-btn-${it.id}`}><Pencil className="h-4 w-4" /></Button>}
                      </td>
                    </tr>
                  );
                })}
              </Table>
            )}
          </Panel>
        </TabsContent>
        {stockPerm && (
          <TabsContent value="moves">
            <Panel>
              {!moves.data?.length ? <Empty text="Aucun mouvement" /> : (
                <Table head={["Date", "Article", "Type", "Qté", "Motif", "Par"]} testId="moves-table">
                  {moves.data.map((m) => (
                    <tr key={m.id} className="border-b border-border/60">
                      <td className={`${cellCls} text-muted-foreground`}>{fmtDate(m.at)}</td>
                      <td className={cellCls}>{m.label}</td>
                      <td className={cellCls}>{m.type === "entree" ? <span className="flex items-center gap-1 text-emerald-400"><ArrowDownToLine className="h-3.5 w-3.5" />Entrée</span> : <span className="flex items-center gap-1 text-amber-400"><ArrowUpFromLine className="h-3.5 w-3.5" />Sortie</span>}</td>
                      <td className={`${cellCls} font-mono-data`}>{m.qty}</td>
                      <td className={cellCls}>{m.reason}</td>
                      <td className={`${cellCls} text-muted-foreground`}>{m.by}</td>
                    </tr>
                  ))}
                </Table>
              )}
            </Panel>
          </TabsContent>
        )}
      </Tabs>
      <ItemDialog item={edit} open={edit !== undefined} onOpenChange={(o: boolean) => !o && setEdit(undefined)} onSaved={refresh} tva={settings.data?.tva_rate ?? 19} categories={categories} />
      <MoveDialog item={move} onClose={() => setMove(null)} onSaved={refresh} />
      <BulkStockDialog items={items.data || []} open={bulk} onOpenChange={setBulk} onSaved={refresh} />
    </div>
  );
}
